import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  Modal,
  Space,
  Typography,
} from "antd";
import RoleService from "../../services/RoleService";
import PermissionService from "../../services/PermissonService";
import { openNotification } from "../../utils";

interface RoleEditModalProps {
  open: boolean;
  roleId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface PermissionItem {
  id: number;
  name: string;
  title?: string;
  team?: string;
  description?: string;
}

const resolveTeam = (permission: PermissionItem) => {
  if (permission.team) return permission.team;
  const prefix = permission.name.split(":")[0];
  if (prefix.startsWith("mongerlab-customer-")) return "Mongerlab Settings";
  if (prefix === "mongerlab-customers") return "Mongerlab";
  const map: Record<string, string> = {
    users: "Users",
    customers: "Customers",
    "customer-orders": "Customer Orders",
    learners: "Learners",
    "edu-learners": "Edu",
    mongerlab: "Mongerlab",
    scheduler: "Scheduler",
    tasks: "Tasks",
    roles: "Roles",
    permissions: "Permissions",
  };
  return map[prefix] || "Other";
};

const RoleEditModal: React.FC<RoleEditModalProps> = ({
  open,
  roleId,
  onClose,
  onSuccess,
}) => {
  const isCreateMode = roleId === null;
  const [form] = Form.useForm();
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setLoading(true);

    if (isCreateMode) {
      form.resetFields();
      setSelectedIds([]);
      PermissionService.getAll()
        .then((permRes) => setPermissions(permRes.data))
        .catch(console.log)
        .finally(() => setLoading(false));
      return;
    }

    Promise.all([PermissionService.getAll(), RoleService.get(roleId)])
      .then(([permRes, roleRes]) => {
        setPermissions(permRes.data);
        form.setFieldsValue({
          name: roleRes.data.name,
          description: roleRes.data.description,
        });
        setSelectedIds((roleRes.data.permissions || []).map((p: any) => p.id));
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [open, roleId, isCreateMode, form]);

  const groupedPermissions = useMemo(() => {
    const filtered = permissions.filter((permission) => {
      const q = search.toLowerCase();
      return (
        permission.name.toLowerCase().includes(q) ||
        (permission.title || "").toLowerCase().includes(q) ||
        (permission.team || "").toLowerCase().includes(q)
      );
    });

    const groups: Record<string, PermissionItem[]> = {};
    filtered.forEach((permission) => {
      const team = resolveTeam(permission);
      if (!groups[team]) groups[team] = [];
      groups[team].push(permission);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, search]);

  const toggleGroup = (teamPermissions: PermissionItem[], checked: boolean) => {
    const ids = teamPermissions.map((p) => p.id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isCreateMode) {
        const createRes = await RoleService.create({
          name: values.name.trim().toUpperCase().replace(/\s+/g, "_"),
          description: values.description?.trim() || values.name.trim(),
        });
        await RoleService.update(createRes.data.id, {
          name: createRes.data.name,
          description: createRes.data.description,
          permissionIds: selectedIds,
        });
        openNotification("success", "Başarılı", "Rol oluşturuldu");
      } else if (roleId) {
        await RoleService.update(roleId, {
          name: values.name.trim(),
          description: values.description?.trim() || values.name.trim(),
          permissionIds: selectedIds,
        });
        openNotification("success", "Başarılı", "Rol güncellendi");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      if (error?.errorFields) return;
      const message = error?.response?.data?.message || "İşlem başarısız";
      openNotification("error", "Hata", message);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = isCreateMode
    ? "Yeni Rol"
    : `Rol Yetkileri — ${form.getFieldValue("description") || ""}`;

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      width={900}
      destroyOnClose
      footer={
        <Space>
          <Typography.Text type="secondary">
            {selectedIds.length} / {permissions.length} yetki seçili
          </Typography.Text>
          <Button onClick={onClose}>İptal</Button>
          <Button type="primary" loading={loading} onClick={save}>
            {isCreateMode ? "Oluştur" : "Kaydet"}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item
          name="name"
          label="Rol kodu"
          rules={[{ required: true, message: "Rol kodu zorunludur" }]}
          extra="Örn: CUSTOM_SALES_TEAM (boşluklar alt çizgiye dönüşür)"
        >
          <Input placeholder="CUSTOM_SALES_TEAM" disabled={!isCreateMode} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Açıklama"
          rules={[{ required: true, message: "Açıklama zorunludur" }]}
        >
          <Input placeholder="Özel Satış Ekibi" />
        </Form.Item>
      </Form>

      <Input.Search
        placeholder="Yetki ara..."
        allowClear
        style={{ marginBottom: 16 }}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Collapse
        accordion={false}
        items={groupedPermissions.map(([team, teamPermissions]) => {
          const teamIds = teamPermissions.map((p) => p.id);
          const allSelected = teamIds.every((id) => selectedIds.includes(id));
          const partiallySelected =
            !allSelected && teamIds.some((id) => selectedIds.includes(id));

          return {
            key: team,
            label: (
              <Space>
                <Checkbox
                  checked={allSelected}
                  indeterminate={partiallySelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggleGroup(teamPermissions, e.target.checked)}
                />
                <strong>{team}</strong>
                <Typography.Text type="secondary">
                  ({teamPermissions.length})
                </Typography.Text>
              </Space>
            ),
            children: (
              <Checkbox.Group
                style={{ width: "100%" }}
                value={selectedIds}
                onChange={(values) => setSelectedIds(values as number[])}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {teamPermissions.map((permission) => (
                    <Checkbox key={permission.id} value={permission.id}>
                      {permission.title || permission.name}
                      <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                        {permission.name}
                      </Typography.Text>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            ),
          };
        })}
      />
    </Modal>
  );
};

export default RoleEditModal;
